import React, { useState, useRef, useEffect } from 'react';
import { apiFetch } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../CentralSnackbar';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  ListItemText
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon, 
  UploadFile as UploadFileIcon, 
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  RemoveCircleOutline as UnassignIcon,
  Download as DownloadIcon,
  Assignment as ScanProtocolsIcon
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:25000';

// Regionen mit gekoppelten Indizes
const REGIONS = [
  { name: 'Kopf', index: 1 },
  { name: 'Hals', index: 2 },
  { name: 'Thorax', index: 3 },
  { name: 'Wirbelsäule', index: 4 },
  { name: 'Abdomen', index: 5 },
  { name: 'Becken', index: 6 },
  { name: 'Obere Extremitäten', index: 7 },
  { name: 'Untere Extremitäten', index: 8 },
  { name: 'Mammo', index: 9 },
  { name: 'Ganzkörper', index: 10 },
  { name: 'Gefäße', index: 11 }
];

const ScanProtocols = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useSnackbar();
  const [scanProtocols, setScanProtocols] = useState([]);
  const [examinations, setExaminations] = useState([]);
  const [search, setSearch] = useState('');
  const [modalityFilter, setModalityFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEditProtocol, setDialogEditProtocol] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedExaminations, setSelectedExaminations] = useState([]);
  const fileInputRef = useRef();

  // Formular-States für Add/Edit Dialog
  const [formData, setFormData] = useState({
    modality: '',
    name: '',
    description: '',
    region: '',
    index: 0
  });

  // Reset form data when dialog opens/closes
  const resetFormData = () => {
    setFormData({
      modality: '',
      name: '',
      description: '',
      region: '',
      index: 0
    });
  };

  useEffect(() => {
    fetchScanProtocols();
    fetchExaminations();
    // eslint-disable-next-line
  }, [search, modalityFilter, regionFilter]);

  // Lade Untersuchungsprotokolle vom Backend
  const fetchScanProtocols = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (modalityFilter) params.append('modality', modalityFilter);
      if (regionFilter) params.append('region', regionFilter);
      const res = await apiFetch('/admin/scan-protocols?${params.toString()}');
      if (res.ok) {
        let data = await res.json();
        // Sortierung: erst nach Index (aufsteigend), dann nach Modalität (a->z)
        data = data.sort((a, b) => {
          if ((a.index || 0) < (b.index || 0)) return -1;
          if ((a.index || 0) > (b.index || 0)) return 1;
          if ((a.modality || '').toLowerCase() < (b.modality || '').toLowerCase()) return -1;
          if ((a.modality || '').toLowerCase() > (b.modality || '').toLowerCase()) return 1;
          return 0;
        });
        setScanProtocols(data);
      }
    } catch (e) {
      showError(t('admin.reportingManagement.scanProtocols.loadError'), 'ERR-SCANPROT-001');
    } finally {
      setLoading(false);
    }
  };

  // Lade Untersuchungen für Zuordnung
  const fetchExaminations = async () => {
    try {
      const res = await apiFetch('/admin/examinations');
      if (res.ok) {
        const data = await res.json();
        setExaminations(data);
      }
    } catch (e) {
      console.error('Error loading examinations:', e);
    }
  };

  // --- UI-RENDER ---
  const hasSelected = selected.length > 0;
  const exactlyOneSelected = selected.length === 1;

  // Checkbox-Handler
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(scanProtocols.map(protocol => protocol.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (protocolId) => {
    setSelected(prev => 
      prev.includes(protocolId) 
        ? prev.filter(id => id !== protocolId)
        : [...prev, protocolId]
    );
  };

  // Button-Handler
  const handleOpenAdd = () => {
    resetFormData();
    setDialogEditProtocol(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = () => {
    const selectedProtocol = scanProtocols.find(protocol => protocol.id === selected[0]);
    setDialogEditProtocol(selectedProtocol);
    setFormData({
      modality: selectedProtocol.modality || '',
      name: selectedProtocol.name || '',
      description: selectedProtocol.description || '',
      region: selectedProtocol.region || '',
      index: selectedProtocol.index || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (selected.length === 1) {
      const selectedProtocol = scanProtocols.find(protocol => protocol.id === selected[0]);
      setDeleteTarget(selectedProtocol);
    } else {
      setDeleteTarget({ count: selected.length });
    }
    setDeleteDialogOpen(true);
  };

  const handleAssign = () => {
    setAssignDialogOpen(true);
  };

  const handleUnassign = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/scan-protocols/unassign-standard`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          protocol_ids: selected
        })
      });

      if (response.ok) {
        showSuccess(t('admin.reportingManagement.scanProtocols.unassignSuccess'));
        fetchScanProtocols();
        setSelected([]);
      } else {
        const errorData = await response.json();
        showError(errorData.detail || t('admin.reportingManagement.scanProtocols.unassignError'), 'ERR-SCANPROT-002');
      }
    } catch (error) {
      showError(t('admin.reportingManagement.scanProtocols.unassignError'), 'ERR-SCANPROT-003');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleCsvUpload(file);
    }
  };

  const handleCsvUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);

      const response = await fetch(`${API_BASE}/admin/scan-protocols/upload-csv`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        
        const message = result.imported > 0 
          ? t('admin.reportingManagement.scanProtocols.uploadSuccess', { count: result.imported })
          : t('admin.reportingManagement.scanProtocols.uploadNoNew');
        showSuccess(message);
        
        fetchScanProtocols();
      } else {
        const errorData = await response.json();
        showError(errorData.detail || t('admin.reportingManagement.scanProtocols.uploadError'), 'ERR-SCANPROT-004');
      }
    } catch (error) {
      showError(t('admin.reportingManagement.scanProtocols.uploadError'), 'ERR-SCANPROT-005');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const url = dialogEditProtocol 
        ? `${API_BASE}/admin/scan-protocols/${dialogEditProtocol.id}`
        : `${API_BASE}/admin/scan-protocols`;
      
      const method = dialogEditProtocol ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setDialogOpen(false);
        showSuccess(dialogEditProtocol ? t('admin.reportingManagement.scanProtocols.saveSuccess') : t('admin.reportingManagement.scanProtocols.createSuccess'));
        fetchScanProtocols();
        setSelected([]);
      } else {
        const errorData = await response.json();
        showError(errorData.detail || t('admin.reportingManagement.scanProtocols.saveError'), 'ERR-SCANPROT-006');
      }
    } catch (error) {
      showError(t('admin.reportingManagement.scanProtocols.saveError'), 'ERR-SCANPROT-007');
    }
  };

  const handleCsvTemplateDownload = () => {
    // CSV Header definieren
    const headers = [
      'modality',
      'name', 
      'description',
      'region',
      'index'
    ];

    // Deutsche Header für Benutzerfreundlichkeit
    const germanHeaders = [
      'Modalität',
      'Name',
      'Beschreibung',
      'Region',
      'Index'
    ];

    // CSV Inhalt erstellen mit BOM für korrekte Darstellung in Excel
    const csvContent = '\uFEFF' + [
      headers.join(';'), // Technische Header (werden vom Backend verarbeitet)
      '# ' + germanHeaders.join(';'), // Deutsche Header als Kommentarzeile
      '# Beispiel: CT;Standard Thorax;Routine Thorax Protokoll;Thorax;1',
      '# Vorhandene Protokolle (gleicher Name und Modalität) werden automatisch übersprungen',
      ''
    ].join('\n');

    // CSV Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'scan_protokolle_vorlage.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeletingLoading(true);
      
      const protocolsToDelete = selected.length === 1 ? [deleteTarget.id] : selected;
      let successCount = 0;
      let errors = [];
      
      for (const protocolId of protocolsToDelete) {
        try {
          const response = await fetch(`${API_BASE}/admin/scan-protocols/${protocolId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
          });

          if (response.ok) {
            successCount++;
          } else {
            const errorData = await response.json();
            errors.push(`Protokoll ${protocolId}: ${errorData.detail || t('common.error')}`);
          }
        } catch (error) {
          errors.push(`Protokoll ${protocolId}: ${t('common.error')}`);
        }
      }

      setDeleteDialogOpen(false);

      if (successCount > 0) {
        if (successCount === protocolsToDelete.length) {
          showSuccess(protocolsToDelete.length === 1 
            ? t('common.deleteSuccess') 
            : t('admin.reportingManagement.scanProtocols.deleteMultipleSuccess', { count: successCount }));
        } else {
          showWarning(t('admin.reportingManagement.scanProtocols.deletePartialSuccess', { 
            success: successCount, 
            total: protocolsToDelete.length 
          }));
          showError(errors.join(', '), 'ERR-SCANPROT-008');
        }
      } else {
        showError(errors.join(', '), 'ERR-SCANPROT-009');
      }

      fetchScanProtocols();
      setSelected([]);
    } catch (error) {
      showError(t('admin.reportingManagement.scanProtocols.deleteError'), 'ERR-SCANPROT-010');
    } finally {
      setDeletingLoading(false);
    }
  };

  const handleConfirmAssign = async () => {
    if (selectedExaminations.length === 0) {
      showWarning(t('admin.reportingManagement.scanProtocols.selectExamination'));
      return;
    }

    try {
      // Zuweisungen für jede ausgewählte Untersuchung durchführen
      for (const examinationId of selectedExaminations) {
        const response = await fetch(`${API_BASE}/admin/scan-protocols/assign-standard`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            protocol_ids: selected,
            examination_id: examinationId
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          showError(errorData.detail || t('admin.reportingManagement.scanProtocols.assignError'), 'ERR-SCANPROT-011');
          return;
        }
      }

      setAssignDialogOpen(false);
      showSuccess(t('admin.reportingManagement.scanProtocols.assignSuccess'));
      setSelectedExaminations([]);
      fetchScanProtocols();
      setSelected([]);
    } catch (error) {
      showError(t('admin.reportingManagement.scanProtocols.assignError'), 'ERR-SCANPROT-012');
    }
  };

  // Hilfsfunktion um Standard-für anzuzeigen
  const getStandardFor = (standardForJson) => {
    if (!standardForJson) return null;
    try {
      const standardList = JSON.parse(standardForJson);
      if (!Array.isArray(standardList) || standardList.length === 0) return null;
      
      return standardList.map(examId => {
        const exam = examinations.find(e => e.id === examId);
        return {
          id: examId,
          name: exam ? exam.name : `ID: ${examId}`
        };
      });
    } catch (e) {
      return null;
    }
  };

  // Verfügbare Modalitäten und Regionen aus den Daten extrahieren
  const availableModalities = [...new Set(scanProtocols.map(p => p.modality).filter(Boolean))].sort();
  const availableRegions = [...new Set(scanProtocols.map(p => p.region).filter(Boolean))].sort();

  // Gefilterte Untersuchungen für Zuordnungsdialog basierend auf ausgewählten Protokollen
  const getFilteredExaminations = () => {
    if (selected.length === 0) return [];
    
    const selectedProtocols = scanProtocols.filter(p => selected.includes(p.id));
    const uniqueModalities = [...new Set(selectedProtocols.map(p => p.modality).filter(Boolean))];
    const uniqueRegions = [...new Set(selectedProtocols.map(p => p.region).filter(Boolean))];
    
    return examinations.filter(exam => 
      uniqueModalities.includes(exam.modality) && uniqueRegions.includes(exam.region)
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScanProtocolsIcon color="primary" />
          {t('admin.reportingManagement.scanProtocols.title')}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            className="button"
            disabled={loading || deletingLoading}
          >
            {t('common.add')}
          </Button>
          <Button
            className="button"
            variant="contained"
            startIcon={<UploadFileIcon />}
            component="label"
            disabled={loading || deletingLoading}
          >
            {t('admin.reportingManagement.scanProtocols.uploadCsv')}
            <input
              type="file"
              accept=".csv"
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </Button>
          <Button
            className="button"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleCsvTemplateDownload}
            disabled={loading || deletingLoading}
          >
            {t('admin.reportingManagement.scanProtocols.csvTemplate')}
          </Button>
        </Box>
      </Box>

      {/* Aktionsleiste */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ mr: 2 }}>
              {hasSelected
                ? t('admin.reportingManagement.scanProtocols.selected', { count: selected.length })
                : t('admin.reportingManagement.scanProtocols.noSelected')
              }
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenEdit}
                  disabled={!exactlyOneSelected || loading || deletingLoading}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDelete}
                  disabled={!hasSelected || loading || deletingLoading}
                >
                  {deletingLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={t('admin.reportingManagement.scanProtocols.assign')}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleAssign}
                  disabled={!hasSelected || loading || deletingLoading}
                >
                  <AssignmentIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('admin.reportingManagement.scanProtocols.unassign')}>
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  onClick={handleUnassign}
                  disabled={!hasSelected || loading || deletingLoading}
                >
                  <UnassignIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          
          {/* Filter-Bereich */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('admin.reportingManagement.scanProtocols.modality')}</InputLabel>
              <Select
                value={modalityFilter}
                label={t('admin.reportingManagement.scanProtocols.modality')}
                onChange={(e) => setModalityFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('common.all')}</em>
                </MenuItem>
                {availableModalities.map((modality) => (
                  <MenuItem key={modality} value={modality}>
                    {modality}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('admin.reportingManagement.scanProtocols.region')}</InputLabel>
              <Select
                value={regionFilter}
                label={t('admin.reportingManagement.scanProtocols.region')}
                onChange={(e) => setRegionFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('common.all')}</em>
                </MenuItem>
                {availableRegions.map((region) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label={t('admin.reportingManagement.scanProtocols.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Tabelle */}
      <Paper sx={{ width: '100%' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < scanProtocols.length}
                    checked={scanProtocols.length > 0 && selected.length === scanProtocols.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2, width: 'auto' }}>{t('admin.reportingManagement.scanProtocols.modality')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2, width: 'auto' }}>{t('admin.reportingManagement.scanProtocols.region')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2, width: 'auto' }}>{t('admin.reportingManagement.scanProtocols.name')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2, width: '100%' }}>{t('admin.reportingManagement.scanProtocols.description')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2, width: '400px', maxWidth: '400px' }}>{t('admin.reportingManagement.scanProtocols.standardFor')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : scanProtocols.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('admin.reportingManagement.scanProtocols.noResults')}
                  </TableCell>
                </TableRow>
              ) : (
                scanProtocols.map(protocol => (
                  <TableRow key={protocol.id} selected={selected.includes(protocol.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(protocol.id)}
                        onChange={() => handleSelect(protocol.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>{protocol.modality}</TableCell>
                    <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>{protocol.region || '-'}</TableCell>
                    <TableCell sx={{ width: 'auto', whiteSpace: 'nowrap' }}>{protocol.name}</TableCell>
                    <TableCell sx={{ 
                      width: '100%',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word'
                    }}>
                      {protocol.description || '-'}
                    </TableCell>
                    <TableCell sx={{ width: '400px', maxWidth: '400px' }}>
                      {(() => {
                        const standardFor = getStandardFor(protocol.standard_for);
                        if (!standardFor || standardFor.length === 0) return '-';
                        
                        return (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 0.5,
                            maxHeight: '120px',
                            overflow: 'auto'
                          }}>
                            {standardFor.map((item) => (
                              <Chip
                                key={item.id}
                                label={item.name}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ 
                                  alignSelf: 'flex-start',
                                  maxWidth: '100%',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogEditProtocol ? t('admin.reportingManagement.scanProtocols.editTitle') : t('admin.reportingManagement.scanProtocols.addTitle')}
        </DialogTitle>
        <DialogContent sx={{ p: 3, height: '400px', overflow: 'hidden' }}>
          <Box sx={{ 
            display: 'flex',
            height: '100%',
            width: '100%'
          }}>
            {/* Regionen - Links */}
            <Box sx={{ 
              width: '180px',
              flexShrink: 0,
              pr: 2
            }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 2, fontSize: '0.9rem' }}>
                {t('admin.reportingManagement.scanProtocols.region')}
              </Typography>
              <Box sx={{ 
                height: 'calc(100% - 30px)',
                overflow: 'auto'
              }}>
                {REGIONS.map((region) => (
                  <Box
                    key={region.index}
                    onClick={() => setFormData({ 
                      ...formData, 
                      region: region.name,
                      index: region.index
                    })}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      borderRadius: 1,
                      mb: 0.5,
                      fontSize: '0.85rem',
                      border: formData.region === region.name ? '2px solid var(--rd-primary)' : '1px solid var(--rd-divider)',
                      backgroundColor: formData.region === region.name ? 'var(--rd-primary)' : 'transparent',
                      color: formData.region === region.name ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: formData.region === region.name ? 'rgb(224, 132, 0)' : 'grey.100'
                      }
                    }}
                  >
                    <Box fontWeight="bold">{region.name}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
            
            {/* Trennlinie - Mitte */}
            <Box sx={{ 
              width: '20px',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              pt: '30px'
            }}>
              <Box sx={{ 
                width: '2px', 
                backgroundColor: 'var(--rd-primary)',
                height: 'calc(100% - 30px)'
              }} />
            </Box>
            
            {/* Formular - Rechts */}
            <Box sx={{ 
              flex: 1,
              pl: 2,
              minWidth: 0
            }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 2, fontSize: '0.9rem' }}>
                Protokoll-Details
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2,
                height: 'calc(100% - 30px)'
              }}>
                <TextField
                  label={t('admin.reportingManagement.scanProtocols.modality')}
                  value={formData.modality}
                  onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
                  fullWidth
                  required
                  size="small"
                />
                
                <TextField
                  label={t('admin.reportingManagement.scanProtocols.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                  size="small"
                />
                
                <TextField
                  label={t('admin.reportingManagement.scanProtocols.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                />
                
                {/* Anzeige der ausgewählten Region */}
                {formData.region && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Ausgewählte Region:</strong> {formData.region}
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} className="button-cancel">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            className="button"
            disabled={!formData.modality || !formData.name || !formData.region}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {t('common.delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget && (
              deleteTarget.count 
                ? t('admin.reportingManagement.scanProtocols.deleteConfirmMultiple', { 
                    count: deleteTarget.count 
                  })
                : t('admin.reportingManagement.scanProtocols.deleteConfirmSingle', { 
                            name: deleteTarget.name || 'Untersuchungsprotokoll'
      }).replace(/'{name}'/, `"${deleteTarget.name || 'Untersuchungsprotokoll'}"`)
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            className="button-cancel"
            disabled={deletingLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            className="button-delete"
            disabled={deletingLoading}
            startIcon={deletingLoading ? <CircularProgress size={20} /> : null}
          >
            {deletingLoading ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('admin.reportingManagement.scanProtocols.assignTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.reportingManagement.scanProtocols.assignDescription', { count: selected.length })}
          </DialogContentText>
          
          {/* Filterinformationen anzeigen */}
          {selected.length > 0 && (() => {
            const selectedProtocols = scanProtocols.filter(p => selected.includes(p.id));
            const uniqueModalities = [...new Set(selectedProtocols.map(p => p.modality).filter(Boolean))];
            const uniqueRegions = [...new Set(selectedProtocols.map(p => p.region).filter(Boolean))];
            const filteredExaminations = getFilteredExaminations();
            
            return (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Filter:</strong> Modalität: {uniqueModalities.join(', ')} | Region: {uniqueRegions.join(', ')}
                </Typography>
                <Typography variant="body2">
                  <strong>{filteredExaminations.length}</strong> passende Untersuchungen gefunden
                </Typography>
              </Alert>
            );
          })()}
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('admin.reportingManagement.scanProtocols.selectExamination')}</InputLabel>
            <Select
              multiple
              value={selectedExaminations}
              label={t('admin.reportingManagement.scanProtocols.selectExamination')}
              onChange={(e) => setSelectedExaminations(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((examId) => {
                    const exam = examinations.find(e => e.id === examId);
                    return (
                      <Chip
                        key={examId}
                        label={exam ? exam.name : `ID: ${examId}`}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {getFilteredExaminations().map(exam => (
                <MenuItem key={exam.id} value={exam.id}>
                  <Checkbox checked={selectedExaminations.indexOf(exam.id) > -1} />
                  <ListItemText primary={`${exam.name} (${exam.modality} - ${exam.region})`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Warnung wenn keine passenden Untersuchungen */}
          {selected.length > 0 && getFilteredExaminations().length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Keine Untersuchungen gefunden, die sowohl in Modalität als auch Region zu den ausgewählten Protokollen passen.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} className="button-cancel">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmAssign} 
            className="button"
            disabled={selectedExaminations.length === 0 || getFilteredExaminations().length === 0}
          >
            {t('admin.reportingManagement.scanProtocols.assign')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScanProtocols; 